import { dayKey } from '$features/forecast';
import type { MacroSession, RecallItem, ReviewEvent } from '$domain/types';
import type {
  AnalyticsSummary,
  ForgettingCurve,
  ScatterPoint,
  YearHeatmapDay,
  YearHeatmapWeek
} from './types';

const FSRS_DECAY = -0.5;
const FSRS_FACTOR = 19 / 81;

export function fsrsRetrievability(t: number, stability: number): number {
  if (stability <= 0) return 0;
  return Math.pow(1 + FSRS_FACTOR * (t / stability), FSRS_DECAY);
}

export function computeScatter(items: RecallItem[]): ScatterPoint[] {
  return items.map((i) => ({
    stability: i.stability,
    difficulty: i.difficulty,
    reps: i.reps,
    state: i.state
  }));
}

export function computeForgettingCurves(
  stabilities: number[] = [0.5, 1, 5, 25, 100, 365],
  maxDays: number = 60,
  samples: number = 60
): ForgettingCurve[] {
  const step = maxDays / samples;
  return stabilities.map((s) => {
    const points: Array<{ t: number; r: number }> = [];
    for (let i = 0; i <= samples; i++) {
      const t = i * step;
      points.push({ t, r: fsrsRetrievability(t, s) });
    }
    return {
      stability: s,
      label: s < 1 ? `${s.toFixed(1)}d` : `${Math.round(s)}d`,
      points
    };
  });
}

function heatLevelFromMinutes(minutes: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (minutes <= 0 || max <= 0) return 0;
  const ratio = minutes / max;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

function minutesByDay(sessions: MacroSession[]): Map<string, number> {
  const map = new Map<string, number>();
  for (const s of sessions) {
    if (s.status !== 'finished') continue;
    const k = dayKey(new Date(s.startedAt));
    const prev = map.get(k) ?? 0;
    map.set(k, prev + s.focusDurationMs / 60000);
  }
  return map;
}

export function buildYearHeatmap(
  sessions: MacroSession[],
  today: Date = new Date(),
  weeksBack: number = 53
): { weeks: YearHeatmapWeek[]; maxMinutes: number } {
  const byDay = minutesByDay(sessions);
  const todayMid = new Date(today);
  todayMid.setHours(0, 0, 0, 0);

  // Align to Sunday (day 0)
  const startOffset = todayMid.getDay();
  const gridStart = new Date(todayMid);
  gridStart.setDate(gridStart.getDate() - (weeksBack - 1) * 7 - startOffset);

  const weeks: YearHeatmapWeek[] = [];
  let max = 0;

  for (let w = 0; w < weeksBack; w++) {
    const days: YearHeatmapDay[] = [];
    for (let d = 0; d < 7; d++) {
      const dt = new Date(gridStart);
      dt.setDate(dt.getDate() + w * 7 + d);
      const key = dayKey(dt);
      const isFuture = dt.getTime() > todayMid.getTime();
      const minutes = isFuture ? 0 : Math.round(byDay.get(key) ?? 0);
      if (minutes > max) max = minutes;
      days.push({ date: key, minutes, level: 0, isFuture });
    }
    weeks.push({ days });
  }

  for (const week of weeks) {
    for (const day of week.days) {
      day.level = heatLevelFromMinutes(day.minutes, max);
    }
  }

  return { weeks, maxMinutes: max };
}

export function computeSummary(
  items: RecallItem[],
  events: ReviewEvent[],
  today: Date = new Date()
): AnalyticsSummary {
  const n = items.length;
  if (n === 0) {
    return {
      totalItems: 0,
      activeItems: 0,
      avgStability: 0,
      medianStability: 0,
      avgDifficulty: 0,
      dueToday: 0,
      totalReviews: events.length
    };
  }

  const end = new Date(today);
  end.setHours(23, 59, 59, 999);
  const cutoff = end.getTime();

  const stabilityValues = items.map((i) => i.stability).sort((a, b) => a - b);
  const median =
    n % 2 === 0
      ? ((stabilityValues[n / 2 - 1] ?? 0) + (stabilityValues[n / 2] ?? 0)) / 2
      : (stabilityValues[Math.floor(n / 2)] ?? 0);

  const sumStability = items.reduce((s, i) => s + i.stability, 0);
  const sumDifficulty = items.reduce((s, i) => s + i.difficulty, 0);
  const activeItems = items.filter((i) => i.state === 'active').length;
  const dueToday = items.filter(
    (i) => i.state === 'active' && new Date(i.nextReviewAt).getTime() <= cutoff
  ).length;

  return {
    totalItems: n,
    activeItems,
    avgStability: sumStability / n,
    medianStability: median,
    avgDifficulty: sumDifficulty / n,
    dueToday,
    totalReviews: events.length
  };
}